import {Injectable} from '@angular/core';
import {LocalNotifications, LocalNotificationSchema, Schedule, Weekday} from '@capacitor/local-notifications';
import {MobileService} from '../core/page/mobile.service';

export interface Reminder {
  id: number;
  time: string;
  enabled: boolean;
  label: string;
  days: Weekday[]; // Array of Capacitor Weekday enum values (1=Sunday, 2=Monday, ..., 7=Saturday)
}

@Injectable({
  providedIn: 'root',
})
export class OsNotificationService {
  private baseNotificationId = 1001;
  private activeNotificationIds: number[] = [];
  private readonly CHANNEL_ID = 'reminders';

  constructor(private mobileService: MobileService) {
    const isMobile = this.mobileService.isMobilePlatform();
    if (!isMobile) return;
    // Initialize notification service with base ID and active tracking
    this.createNotificationChannel();
    this.setupNotificationListeners();
  }

  private setupNotificationListeners(): void {
    // Handle notifications received while app is in foreground
    LocalNotifications.addListener('localNotificationReceived', notification => {
      console.log('Notification received in foreground:', JSON.stringify(notification));
    });

    // Handle when user clicks on a notification
    LocalNotifications.addListener('localNotificationActionPerformed', action => {
      console.log('Notification action performed:', JSON.stringify(action));

      // Extract route from notification data if available
      const routeData = action.notification.extra?.route;

      // TODO: just logging for now, implement navigation as needed

      if (routeData) {
        // Navigate to the specified route
        // this.router.navigate([routeData]);
      } else {
        // Default route - navigate to dashboard or home
        // this.router.navigate(['/dashboard']);
      }
    });
  }

  private async createNotificationChannel(): Promise<void> {
    try {
      await LocalNotifications.createChannel({
        id: this.CHANNEL_ID,
        name: 'Reminders',
        description: 'Notification channel for reminders',
        importance: 4, // 4 = default importance
        visibility: 1, // 1 = public
        lightColor: '#FF5722',
        vibration: true,
      });
    } catch (e) {
      console.error('Failed to create notification channel:', e);
    }
  }

  async startDailyReminder(): Promise<void> {
    try {
      const permission = await LocalNotifications.checkPermissions();
      let permissionGranted = permission.display === 'granted';

      if (!permissionGranted) {
        const result = await LocalNotifications.requestPermissions();
        permissionGranted = result.display === 'granted';
      }

      if (permissionGranted) {
        const savedReminders = localStorage.getItem('notificationReminders');
        if (savedReminders) {
          const reminders: Reminder[] = JSON.parse(savedReminders);
          await this.scheduleMultipleNotifications(reminders);
        }
        // This is commented so we don't schedule a default notification if nothing is configured
        // await this.scheduleDailyNotification();
      }
    } catch (e) {
      console.log('Failed to start daily reminder:', e);
    }
  }

  async stopAllReminders(): Promise<void> {
    try {
      if (this.activeNotificationIds.length > 0) {
        await LocalNotifications.cancel({notifications: this.activeNotificationIds.map(id => ({id}))});
        this.activeNotificationIds = [];
      }
    } catch (e) {
      console.log('failed to cancel notifications: ', e);
    }
  }

  async stopDailyReminder(): Promise<void> {
    try {
      await LocalNotifications.cancel({notifications: [{id: this.baseNotificationId}]});
    } catch (e) {
      console.log('failed to cancel notification: ', e);
    }
  }

  async checkPermissions() {
    return await LocalNotifications.checkPermissions();
  }

  async requestPermissions() {
    return await LocalNotifications.requestPermissions();
  }

  async ensureNotificationPermissions(): Promise<boolean> {
    const permission = await LocalNotifications.checkPermissions();
    let permissionGranted = permission.display === 'granted';

    if (!permissionGranted) {
      const result = await LocalNotifications.requestPermissions();
      permissionGranted = result.display === 'granted';
    }

    return permissionGranted;
  }

  async sendTestNotification(): Promise<void> {
    try {
      const permission = await LocalNotifications.checkPermissions();

      let permissionGranted = permission.display === 'granted';

      if (!permissionGranted) {
        const result = await LocalNotifications.requestPermissions();
        permissionGranted = result.display === 'granted';
      }

      if (permissionGranted) {
        // Schedule test notification for 2 seconds from now
        const testTime = new Date();
        testTime.setSeconds(testTime.getSeconds() + 2);

        const notification: LocalNotificationSchema = {
          id: 9999,
          title: 'Test Notification',
          body: 'This is a test notification - it worked!',
          largeBody: 'This is a test notification - it worked!',
          schedule: {
            at: testTime,
          },
          smallIcon: 'ic_notification',
          iconColor: '#FFFFFF',
          channelId: this.CHANNEL_ID,
          autoCancel: true,
          extra: {
            route: '/dashboard',
          },
        };

        const result = await LocalNotifications.schedule({
          notifications: [notification],
        });

        console.log('Test notification scheduled result:', JSON.stringify(result));
      } else {
        console.error('Permission not granted for notifications');
      }
    } catch (e) {
      console.error('Failed to send test notification:', e);
    }
  }

  async scheduleMultipleNotifications(reminders: Reminder[]): Promise<void> {
    await this.stopAllReminders();

    const enabledReminders = reminders.filter(r => r.enabled);
    this.activeNotificationIds = [];

    const notifications: LocalNotificationSchema[] = [];

    for (const reminder of enabledReminders) {
      const [targetHours, targetMinutes] = reminder.time.split(':').map(Number);

      for (const dayOfWeek of reminder.days) {
        const notificationId = this.baseNotificationId + reminder.id * 10 + dayOfWeek;
        const schedule: Schedule = this.getNextScheduledTime(targetHours, targetMinutes, dayOfWeek);

        const notification: LocalNotificationSchema = {
          id: notificationId,
          title: reminder.label,
          body: "Don't forget to check your tasks for today!",
          schedule,
          smallIcon: 'ic_notification',
          largeBody: "Don't forget to check your tasks for today!",
          channelId: this.CHANNEL_ID,
          autoCancel: true,
        };

        notifications.push(notification);
        this.activeNotificationIds.push(notificationId);
      }
    }

    try {
      if (notifications.length > 0) {
        const result = await LocalNotifications.schedule({notifications});
        console.log('Schedule result:', JSON.stringify(result));
      } else {
        console.log('No enabled reminders to schedule');
      }
    } catch (e) {
      console.error('Failed to schedule multiple notifications:', e);
      console.error('Error details:', JSON.stringify(e));
    }
  }

  async scheduleDailyNotification(): Promise<void> {
    const savedTime = localStorage.getItem('notificationTime') || '09:00';
    const [targetHours, targetMinutes] = savedTime.split(':').map(Number);

    await this.stopDailyReminder();

    try {
      const notification: LocalNotificationSchema = {
        id: this.baseNotificationId,
        title: 'Daily Task Reminder',
        body: "Don't forget to check your tasks for today!",
        schedule: {
          on: {
            hour: targetHours,
            minute: targetMinutes,
          },
        },
        smallIcon: 'ic_notification',
        largeBody: "Don't forget to check your tasks for today!",
        channelId: this.CHANNEL_ID,
      };

      await LocalNotifications.schedule({
        notifications: [notification],
      });
    } catch (e) {
      console.log('Failed to schedule daily notification:', e);
    }
  }

  private getNextScheduledTime(hours: number, minutes: number, weekday: Weekday): Schedule {
    // Use 'on' property to schedule on specific weekday every week
    // weekday is already in Capacitor Weekday enum format (1=Sunday, 2=Monday, ..., 7=Saturday)
    return {
      on: {
        weekday,
        hour: hours,
        minute: minutes,
      },
      allowWhileIdle: true, // Important for Android
    };
  }
}
